/**
 * App.tsx — Root Application Component
 *
 * Sets up the full provider hierarchy:
 *   1. MUI ThemeProvider + CssBaseline — design system
 *   2. QueryClientProvider — React Query server-state
 *   3. BrowserRouter — client-side routing
 *   4. Auth initialization — starts the Firebase auth listener
 *
 * Routing strategy:
 *   - Public routes (/login, /register) accessible without auth
 *   - Protected routes (/dashboard, /my-donations, etc.) require authentication
 *   - Admin routes (/admin) require platform_admin role
 *   - AppShell wraps all routes and provides the navigation chrome
 *   - While auth is loading, ProtectedRoute shows LoadingScreen
 *
 * Code splitting: all page-level components are lazy-loaded so the initial
 * bundle stays lean.
 */
import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { philofundTheme } from '@/theme';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth.store';
import { LoadingScreen } from '@/pages/LoadingScreen';
import { AppShell } from '@/components/AppShell';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { DemoAuthProvider } from '@/demo/DemoAuthProvider';

// ---------------------------------------------------------------------------
// React Query client configuration
// ---------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Retry up to 3 times with exponential backoff; never retry on 4xx
      retry: (failureCount, error) => {
        if (isClientError(error)) return false;
        return failureCount < 3;
      },
      // Keep cached data visible while re-fetching (no loading flash)
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      // Retry once on network errors, but not on validation errors
      retry: (failureCount, error) => {
        if (isClientError(error)) return false;
        return failureCount < 1;
      },
    },
  },
});

function isClientError(error: unknown): boolean {
  if (error instanceof Error && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response;
    const status = response?.status;
    return status !== undefined && status >= 400 && status < 500;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Lazy page imports — code splitting
// ---------------------------------------------------------------------------

// Public landing page — shown at "/"
const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);

// Placeholder pages — will be implemented in Phase 1 features
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

// Auth pages — D.3.1 + D.3.2 (RegisterPage renders inside RegisterRoleGate)
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);

// D.4.2 — access denied page
const UnauthorizedPage = lazy(() =>
  import('@/pages/UnauthorizedPage').then((m) => ({ default: m.UnauthorizedPage })),
);

// D.5.1 — Nonprofit browse page (public)
const OrganizationsPage = lazy(() =>
  import('@/pages/OrganizationsPage').then((m) => ({ default: m.OrganizationsPage })),
);

// I.1.1 — Donor dashboard
const DonorDashboardPage = lazy(() =>
  import('@/pages/DonorDashboardPage').then((m) => ({ default: m.DonorDashboardPage })),
);

// I.2.1 — Donor all-donations page
const DonorDonationsPage = lazy(() =>
  import('@/pages/DonorDonationsPage').then((m) => ({ default: m.DonorDonationsPage })),
);

// I.6.1 — Nonprofit/org admin dashboard
const OrgDashboardPage = lazy(() =>
  import('@/pages/OrgDashboardPage').then((m) => ({ default: m.OrgDashboardPage })),
);

// Platform admin dashboard (role: platform_admin)
const PlatformAdminDashboardPage = lazy(() =>
  import('@/pages/PlatformAdminDashboardPage').then((m) => ({ default: m.PlatformAdminDashboardPage })),
);

// D.6.1 — Nonprofit public profile page (public)
const OrganizationProfilePage = lazy(() =>
  import('@/pages/OrganizationProfilePage').then((m) => ({ default: m.OrganizationProfilePage })),
);

// D.7 — Donation flow
const DonationPage = lazy(() =>
  import('@/pages/DonationPage').then((m) => ({ default: m.DonationPage })),
);

// Interest selection — post-registration flow
const InterestSelectionPage = lazy(() =>
  import('@/pages/InterestSelectionPage').then((m) => ({ default: m.InterestSelectionPage })),
);

// D.3.3 — Forgot password (page existed but was never routed)
const ForgotPasswordPage = lazy(() =>
  import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);

// Donor role pages — settings, receipts library, year-end statement
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const ReceiptsPage = lazy(() =>
  import('@/pages/ReceiptsPage').then((m) => ({ default: m.ReceiptsPage })),
);
const YearEndStatementPage = lazy(() =>
  import('@/pages/YearEndStatementPage').then((m) => ({ default: m.YearEndStatementPage })),
);

// Org-admin role pages — Stripe Connect onboarding, donations received, profile
const StripeOnboardPage = lazy(() =>
  import('@/pages/StripeOnboardPage').then((m) => ({ default: m.StripeOnboardPage })),
);
const DonationsReceivedPage = lazy(() =>
  import('@/pages/DonationsReceivedPage').then((m) => ({ default: m.DonationsReceivedPage })),
);
const OrgProfilePage = lazy(() =>
  import('@/pages/OrgProfilePage').then((m) => ({ default: m.OrgProfilePage })),
);

// Platform-admin role pages — HITL queues, donor search, activity stream
const AdminVerificationQueuePage = lazy(() =>
  import('@/pages/AdminVerificationQueuePage').then((m) => ({ default: m.AdminVerificationQueuePage })),
);
const AdminDisputesPage = lazy(() =>
  import('@/pages/AdminDisputesPage').then((m) => ({ default: m.AdminDisputesPage })),
);
const AdminRefundsPage = lazy(() =>
  import('@/pages/AdminRefundsPage').then((m) => ({ default: m.AdminRefundsPage })),
);
const AdminDonorSearchPage = lazy(() =>
  import('@/pages/AdminDonorSearchPage').then((m) => ({ default: m.AdminDonorSearchPage })),
);
const AdminActivityPage = lazy(() =>
  import('@/pages/AdminActivityPage').then((m) => ({ default: m.AdminActivityPage })),
);

// Role settings pages (org + platform admin; donor settings is /donor/settings)
const OrgSettingsPage = lazy(() =>
  import('@/pages/OrgSettingsPage').then((m) => ({ default: m.OrgSettingsPage })),
);
const AdminSettingsPage = lazy(() =>
  import('@/pages/AdminSettingsPage').then((m) => ({ default: m.AdminSettingsPage })),
);

// RBAC administration (PF.PROFILES.P4): directory, living permission matrix, org team
const AdminUsersPage = lazy(() =>
  import('@/pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminPermissionsPage = lazy(() =>
  import('@/pages/AdminPermissionsPage').then((m) => ({ default: m.AdminPermissionsPage })),
);
const OrgTeamPage = lazy(() =>
  import('@/pages/OrgTeamPage').then((m) => ({ default: m.OrgTeamPage })),
);

// Auth demo flows (ADR-016 A1): TOTP enrollment + passkey (any authenticated role)
const AuthMfaEnrollPage = lazy(() =>
  import('@/pages/AuthMfaEnrollPage').then((m) => ({ default: m.AuthMfaEnrollPage })),
);
const AuthPasskeyPage = lazy(() =>
  import('@/pages/AuthPasskeyPage').then((m) => ({ default: m.AuthPasskeyPage })),
);
const AuthChangePasswordPage = lazy(() =>
  import('@/pages/AuthChangePasswordPage').then((m) => ({ default: m.AuthChangePasswordPage })),
);
const AuthResetPasswordPage = lazy(() =>
  import('@/pages/AuthResetPasswordPage').then((m) => ({ default: m.AuthResetPasswordPage })),
);

// Platform-admin donor detail drill-in
const AdminDonorDetailPage = lazy(() =>
  import('@/pages/AdminDonorDetailPage').then((m) => ({ default: m.AdminDonorDetailPage })),
);

// CSV import wizards (org: historical donations/donors; admin: bulk org intake)
const OrgImportPage = lazy(() =>
  import('@/pages/OrgImportPage').then((m) => ({ default: m.OrgImportPage })),
);
const AdminImportPage = lazy(() =>
  import('@/pages/AdminImportPage').then((m) => ({ default: m.AdminImportPage })),
);

// Public screens — manifest docs/integration/new-screens/public-routes.json
const RegisterRoleGate = lazy(() =>
  import('@/pages/public/RegisterRoleGate').then((m) => ({ default: m.RegisterRoleGate })),
);
const OrgRegistrationPage = lazy(() =>
  import('@/pages/public/OrgRegistrationPage').then((m) => ({ default: m.OrgRegistrationPage })),
);
const TermsOfServicePage = lazy(() =>
  import('@/pages/public/TermsOfServicePage').then((m) => ({ default: m.TermsOfServicePage })),
);
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/public/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })),
);
const GuestReceiptPage = lazy(() =>
  import('@/pages/public/GuestReceiptPage').then((m) => ({ default: m.GuestReceiptPage })),
);
const HowItWorksPage = lazy(() =>
  import('@/pages/public/HowItWorksPage').then((m) => ({ default: m.HowItWorksPage })),
);
const AboutPage = lazy(() =>
  import('@/pages/public/AboutPage').then((m) => ({ default: m.AboutPage })),
);
const DonationSuccessPage = lazy(() =>
  import('@/pages/public/DonationSuccessPage').then((m) => ({ default: m.DonationSuccessPage })),
);
const DonationCancelPage = lazy(() =>
  import('@/pages/public/DonationCancelPage').then((m) => ({ default: m.DonationCancelPage })),
);
const InvitationAcceptPage = lazy(() =>
  import('@/pages/public/InvitationAcceptPage').then((m) => ({ default: m.InvitationAcceptPage })),
);
const AccountRecoveryPage = lazy(() =>
  import('@/pages/public/AccountRecoveryPage').then((m) => ({ default: m.AccountRecoveryPage })),
);

// Donor screens — manifest docs/integration/new-screens/donor-routes.json
const DonorSubscriptionsPage = lazy(() =>
  import('@/pages/donor/DonorSubscriptionsPage').then((m) => ({ default: m.DonorSubscriptionsPage })),
);
const DonorPortfolioPage = lazy(() =>
  import('@/pages/donor/DonorPortfolioPage').then((m) => ({ default: m.DonorPortfolioPage })),
);
const DonorImpactPage = lazy(() =>
  import('@/pages/donor/DonorImpactPage').then((m) => ({ default: m.DonorImpactPage })),
);
const DonorMatchesPage = lazy(() =>
  import('@/pages/donor/DonorMatchesPage').then((m) => ({ default: m.DonorMatchesPage })),
);
const DonorNotificationsPage = lazy(() =>
  import('@/pages/donor/DonorNotificationsPage').then((m) => ({ default: m.DonorNotificationsPage })),
);
const DonorPaymentMethodsPage = lazy(() =>
  import('@/pages/donor/DonorPaymentMethodsPage').then((m) => ({ default: m.DonorPaymentMethodsPage })),
);
const DonorRefundsPage = lazy(() =>
  import('@/pages/donor/DonorRefundsPage').then((m) => ({ default: m.DonorRefundsPage })),
);
const DonorPlanPage = lazy(() =>
  import('@/pages/donor/DonorPlanPage').then((m) => ({ default: m.DonorPlanPage })),
);

// Nonprofit (org_admin) screens — manifest docs/integration/new-screens/org-routes.json
const OrgVerificationPage = lazy(() =>
  import('@/pages/organization/OrgVerificationPage').then((m) => ({ default: m.OrgVerificationPage })),
);
const OrgPayoutsPage = lazy(() =>
  import('@/pages/organization/OrgPayoutsPage').then((m) => ({ default: m.OrgPayoutsPage })),
);
const OrgDonorsPage = lazy(() =>
  import('@/pages/organization/OrgDonorsPage').then((m) => ({ default: m.OrgDonorsPage })),
);
const OrgSubscriptionsPage = lazy(() =>
  import('@/pages/organization/OrgSubscriptionsPage').then((m) => ({ default: m.OrgSubscriptionsPage })),
);
const OrgDocumentsPage = lazy(() =>
  import('@/pages/organization/OrgDocumentsPage').then((m) => ({ default: m.OrgDocumentsPage })),
);
const OrgRefundsPage = lazy(() =>
  import('@/pages/organization/OrgRefundsPage').then((m) => ({ default: m.OrgRefundsPage })),
);
const OrgAnalyticsPage = lazy(() =>
  import('@/pages/organization/OrgAnalyticsPage').then((m) => ({ default: m.OrgAnalyticsPage })),
);
const OrgPlanPage = lazy(() =>
  import('@/pages/organization/OrgPlanPage').then((m) => ({ default: m.OrgPlanPage })),
);
const OrgContentPage = lazy(() =>
  import('@/pages/organization/OrgContentPage').then((m) => ({ default: m.OrgContentPage })),
);

// Platform-admin screens — manifest docs/integration/new-screens/admin-routes.json
const AdminOrganizationsPage = lazy(() =>
  import('@/pages/admin/AdminOrganizationsPage').then((m) => ({ default: m.AdminOrganizationsPage })),
);
const AdminOrganizationDetailPage = lazy(() =>
  import('@/pages/admin/AdminOrganizationDetailPage').then((m) => ({ default: m.AdminOrganizationDetailPage })),
);
const AdminCrmPage = lazy(() =>
  import('@/pages/admin/AdminCrmPage').then((m) => ({ default: m.AdminCrmPage })),
);
const AdminMarketingPage = lazy(() =>
  import('@/pages/admin/AdminMarketingPage').then((m) => ({ default: m.AdminMarketingPage })),
);
const AdminAccountingPage = lazy(() =>
  import('@/pages/admin/AdminAccountingPage').then((m) => ({ default: m.AdminAccountingPage })),
);
const AdminCompliancePage = lazy(() =>
  import('@/pages/admin/AdminCompliancePage').then((m) => ({ default: m.AdminCompliancePage })),
);

// ---------------------------------------------------------------------------
// Auth initializer component
// ---------------------------------------------------------------------------

/**
 * AuthInitializer: starts the Firebase auth state listener.
 * Must be rendered INSIDE QueryClientProvider and BrowserRouter.
 */
function AuthInitializer(): null {
  useAuth();
  return null;
}

/** Param-preserving redirect for the legacy /donate/:orgId path. */
function DonateRedirect(): JSX.Element {
  const { orgId } = useParams();
  return <Navigate to={`/donor/donate/${orgId ?? ''}`} replace />;
}

// ---------------------------------------------------------------------------
// App shell
// ---------------------------------------------------------------------------

/**
 * AppRoutes: renders the route tree.
 * Extracted so the parent can wrap it cleanly.
 *
 * Route structure:
 *   AppShell (layout — navigation chrome for all routes)
 *   ├── / → LandingPage (public marketing page)
 *   ├── /login (public)
 *   ├── /register (public)
 *   ├── /signup → redirect to /register (backwards compat)
 *   ├── /unauthorized (accessible without auth — shown after role failure)
 *   ├── ProtectedRoute (requires authentication)
 *   │   ├── /dashboard
 *   │   ├── /nonprofits
 *   │   ├── /my-donations
 *   │   ├── /organization (org_admin)
 *   │   └── /donations-received (org_admin)
 *   ├── ProtectedRoute (requires platform_admin)
 *   │   └── /admin
 *   └── * → NotFoundPage
 */
function AppRoutes(): JSX.Element {
  // During the initial Firebase auth check, show LoadingScreen immediately
  // before any route matching. This prevents a flash of the login page and
  // ensures the test assertion for isLoading works synchronously.
  const { isLoading } = useAuthStore();
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* ---------------------------------------------------------------- */}
        {/* AppShell wraps ALL routes for consistent navigation chrome        */}
        {/* ---------------------------------------------------------------- */}
        <Route element={<AppShell />}>
          {/* Public landing page */}
          <Route path="/" element={<LandingPage />} />

          {/* Public auth routes */}
          <Route path="/login" element={<LoginPage />} />
          {/* ?role=org redirects to /register/organization; otherwise the donor RegisterPage */}
          <Route path="/register" element={<RegisterRoleGate />} />
          <Route path="/register/organization" element={<OrgRegistrationPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/account-recovery" element={<AccountRecoveryPage />} />
          <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
          {/* Reset link is followed signed-out — PUBLIC */}
          <Route path="/auth/password/reset" element={<AuthResetPasswordPage />} />
          {/* Legacy /signup alias */}
          <Route path="/signup" element={<Navigate to="/register" replace />} />

          {/* Access denied — accessible without auth */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* ---------------------------------------------------------------- */}
          {/* Public nonprofit browse routes — D.5.1, D.6.1                    */}
          {/* ---------------------------------------------------------------- */}
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/organizations/:id" element={<OrganizationProfilePage />} />

          {/* Public information, legal and donation-outcome pages */}
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/receipts/:token" element={<GuestReceiptPage />} />
          {/* Static segments outrank /donate/:orgId, so the Stripe return URLs
              no longer resolve to an organization named "success"/"cancel". */}
          <Route path="/donate/success" element={<DonationSuccessPage />} />
          <Route path="/donate/cancel" element={<DonationCancelPage />} />

          {/* ================================================================ */}
          {/* ROLE-PREFIXED URL STRUCTURE (PRD-021): there is no generic       */}
          {/* /dashboard — every authenticated surface lives under its role:   */}
          {/*   /donor/*        donor                                          */}
          {/*   /organization/* nonprofit org admin (501(c)(3) tenant)         */}
          {/*   /admin/*        PhiloFund platform administration              */}
          {/* Old paths redirect so bookmarks and in-flight links keep working.*/}
          {/* ================================================================ */}

          {/* DONOR family — donor role ONLY (no platform_admin bypass) */}
          <Route element={<ProtectedRoute requiredRoles={['donor']} />}>
            <Route path="/donor" element={<DonorDashboardPage />} />
            <Route path="/donor/donations" element={<DonorDonationsPage />} />
            <Route path="/donor/interests" element={<InterestSelectionPage />} />
            <Route path="/donor/settings" element={<SettingsPage />} />
            <Route path="/donor/receipts" element={<ReceiptsPage />} />
            <Route path="/donor/statements" element={<YearEndStatementPage />} />
            {/* D.7 — Donation flow */}
            <Route path="/donor/donate/:orgId" element={<DonationPage />} />
            <Route path="/donor/subscriptions" element={<DonorSubscriptionsPage />} />
            <Route path="/donor/portfolio" element={<DonorPortfolioPage />} />
            <Route path="/donor/impact" element={<DonorImpactPage />} />
            <Route path="/donor/matches" element={<DonorMatchesPage />} />
            <Route path="/donor/notifications" element={<DonorNotificationsPage />} />
            <Route path="/donor/payment-methods" element={<DonorPaymentMethodsPage />} />
            <Route path="/donor/refunds" element={<DonorRefundsPage />} />
            <Route path="/donor/plan" element={<DonorPlanPage />} />
          </Route>

          {/* Auth flows — any authenticated role (donor/org/platform) */}
          <Route element={<ProtectedRoute requiredRoles={['donor', 'org_admin', 'platform_admin']} />}>
            <Route path="/auth/mfa/enroll" element={<AuthMfaEnrollPage />} />
            <Route path="/auth/passkey" element={<AuthPasskeyPage />} />
            <Route path="/auth/password/change" element={<AuthChangePasswordPage />} />
          </Route>

          {/* Legacy donor paths → role-prefixed homes */}
          <Route path="/dashboard" element={<Navigate to="/donor" replace />} />
          <Route path="/my-donations" element={<Navigate to="/donor/donations" replace />} />
          <Route path="/interests" element={<Navigate to="/donor/interests" replace />} />
          <Route path="/settings" element={<Navigate to="/donor/settings" replace />} />
          <Route path="/receipts" element={<Navigate to="/donor/receipts" replace />} />
          <Route path="/statements" element={<Navigate to="/donor/statements" replace />} />
          <Route path="/donate/:orgId" element={<DonateRedirect />} />
          <Route path="/nonprofits" element={<Navigate to="/organizations" replace />} />

          {/* ORGANIZATION family — tenant-scoped, org_admin role ONLY */}
          <Route element={<ProtectedRoute requiredRoles={['org_admin']} />}>
            <Route path="/organization" element={<OrgDashboardPage />} />
            <Route path="/organization/profile" element={<OrgProfilePage />} />
            <Route path="/organization/donations" element={<DonationsReceivedPage />} />
            <Route path="/organization/stripe" element={<StripeOnboardPage />} />
            <Route path="/organization/settings" element={<OrgSettingsPage />} />
            <Route path="/organization/team" element={<OrgTeamPage />} />
            <Route path="/organization/import" element={<OrgImportPage />} />
            <Route path="/organization/verification" element={<OrgVerificationPage />} />
            <Route path="/organization/payouts" element={<OrgPayoutsPage />} />
            <Route path="/organization/donors" element={<OrgDonorsPage />} />
            <Route path="/organization/subscriptions" element={<OrgSubscriptionsPage />} />
            <Route path="/organization/documents" element={<OrgDocumentsPage />} />
            <Route path="/organization/refunds" element={<OrgRefundsPage />} />
            <Route path="/organization/analytics" element={<OrgAnalyticsPage />} />
            <Route path="/organization/plan" element={<OrgPlanPage />} />
            <Route path="/organization/content" element={<OrgContentPage />} />
          </Route>

          {/* Legacy org paths */}
          <Route path="/donations-received" element={<Navigate to="/organization/donations" replace />} />
          <Route path="/stripe/onboard" element={<Navigate to="/organization/stripe" replace />} />

          {/* PHILOFUND ADMINISTRATION family — cross-tenant, platform_admin ONLY */}
          <Route element={<ProtectedRoute requiredRoles={['platform_admin']} />}>
            <Route path="/admin" element={<PlatformAdminDashboardPage />} />
            <Route path="/admin/verification" element={<AdminVerificationQueuePage />} />
            <Route path="/admin/disputes" element={<AdminDisputesPage />} />
            <Route path="/admin/refunds" element={<AdminRefundsPage />} />
            <Route path="/admin/donors" element={<AdminDonorSearchPage />} />
            <Route path="/admin/donors/:id" element={<AdminDonorDetailPage />} />
            <Route path="/admin/activity" element={<AdminActivityPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/permissions" element={<AdminPermissionsPage />} />
            <Route path="/admin/import" element={<AdminImportPage />} />
            <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
            <Route path="/admin/organizations/:id" element={<AdminOrganizationDetailPage />} />
            <Route path="/admin/crm" element={<AdminCrmPage />} />
            <Route path="/admin/marketing" element={<AdminMarketingPage />} />
            <Route path="/admin/accounting" element={<AdminAccountingPage />} />
            <Route path="/admin/compliance" element={<AdminCompliancePage />} />
          </Route>

          {/* Descriptive alias for the platform-admin family */}
          <Route path="/philofund-administration" element={<Navigate to="/admin" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// App props
// ---------------------------------------------------------------------------

export interface AppProps {
  /**
   * When true, the app uses DemoAuthProvider (fake user + MSW) instead of
   * the real Firebase auth listener. Injected by main.tsx at startup.
   * Defaults to false so tests and normal usage are unaffected.
   */
  isDemoMode?: boolean;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

/**
 * App: top-level component exported for rendering and testing.
 *
 * Named export (not default) so tests can import it without the
 * complexity of default-export mocking.
 */
export function App({ isDemoMode = false }: AppProps): JSX.Element {
  return (
    <ThemeProvider theme={philofundTheme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/*
           * Auth initialisation:
           *   - Normal mode: AuthInitializer starts the Firebase auth listener.
           *   - Demo mode:   DemoAuthProvider injects the fake user and renders
           *                  the "DEMO MODE" banner.
           */}
          {isDemoMode ? <DemoAuthProvider /> : <AuthInitializer />}
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
